/**
 * Whether an 835 claim says the paying payer forwarded it onward itself
 * (crossover). Shared by posting, the auto-post gate and the ERA claim list, so
 * they cannot disagree and strand a forwarded claim on the COB work list.
 *
 * Plain CommonJS like its neighbours: the front-end build has no TypeScript
 * loader for ts-common. Types live in the adjacent .d.ts.
 */

// CLP02 values meaning processed and forwarded on.
const FORWARDED_CLAIM_STATUS_CODES = ['19', '20', '21'];

// MOA remarks announcing a forward. MA01 is appeal rights, on nearly every
// Medicare remit, and is not one of them.
const CROSSOVER_REMARK_CODES = ['MA18', 'N89', 'MA07'];

// What the import writes where a claim named no carrier.
const EMPTY_CARRIER_NAMES = ['', '-'];

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

const hasForwardedStatusCode = (claim) => FORWARDED_CLAIM_STATUS_CODES.indexOf(trimmed(claim.claimStatusCode)) >= 0;

/**
 * NM1*TT named a carrier, by payer id or by name. The name is often the
 * carrier's corporate parent rather than the product on the charge, so it is
 * evidence a forward happened and never a key to match a payer by.
 */
const hasCrossoverCarrier = (claim) => {
    if (trimmed(claim.crossoverPayerId)) {
        return true;
    }

    return EMPTY_CARRIER_NAMES.indexOf(trimmed(claim.nextResponsible)) < 0;
};

const hasCrossoverRemarkCode = (claim) => {
    if (!Array.isArray(claim.remarkCodes)) {
        return false;
    }

    return claim.remarkCodes.some((code) => CROSSOVER_REMARK_CODES.indexOf(trimmed(code)) >= 0);
};

/** The next payer already has this claim, so a COB claim would duplicate it. */
const isForwardedClaim = (claim) => {
    if (!claim) {
        return false;
    }

    return hasForwardedStatusCode(claim)
        || hasCrossoverCarrier(claim)
        || hasCrossoverRemarkCode(claim);
};

/** The signals behind the verdict, for reports and explanations. */
const describeForwardedEvidence = (claim) => {
    const evidence = [];

    if (!claim) {
        return evidence;
    }

    if (hasForwardedStatusCode(claim)) {
        evidence.push(`CLP02 ${trimmed(claim.claimStatusCode)}`);
    }

    if (trimmed(claim.crossoverPayerId)) {
        evidence.push(`NM1*TT payer id ${trimmed(claim.crossoverPayerId)}`);
    } else if (EMPTY_CARRIER_NAMES.indexOf(trimmed(claim.nextResponsible)) < 0) {
        evidence.push(`NM1*TT ${trimmed(claim.nextResponsible)}`);
    }

    if (hasCrossoverRemarkCode(claim)) {
        const matched = claim.remarkCodes.filter(
            (code) => CROSSOVER_REMARK_CODES.indexOf(trimmed(code)) >= 0,
        );

        evidence.push(`MOA ${matched.join(', ')}`);
    }

    return evidence;
};

module.exports = {
    FORWARDED_CLAIM_STATUS_CODES,
    CROSSOVER_REMARK_CODES,
    isForwardedClaim,
    describeForwardedEvidence,
};
